// PostMedia.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import throttle from 'lodash/throttle';
import { color, breakpoint } from '../style/variables.js';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import {
  Swiper,
  SwiperSlide,
  SwiperPaginationDefaultStyle,
} from '../component/Swiper.jsx';

import FadeIn from '../style/FadeIn.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ArrowIconSrc from '../../img/ic_arrow_right_white.svg';
import PostVideoPlayer from '../container/PostVideoPlayer.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { PlayerContext } from '../resource/contexts.js';

import { ButtonId } from '../resource/trackingConstants.js';

const isServer = typeof window === 'undefined';

export class PostMedia extends React.PureComponent {
  state = {
    mediaHeight: '100%',
    activeIndex: 0,
    isMouseMoving: false,
    playerContext: {
      player: null,
      setPlayer: player => {
        this.setState({
          playerContext: {
            ...this.state.playerContext,
            player,
          },
        });
      },
    },
    isThumbnailLoaded: false,
  };
  selfRef = React.createRef();
  swiper = null;
  navigatorDisplayTimeout = null;
  imageElementRef = null;

  componentDidMount() {
    const {
      isIntersecting,
      postId,
      setPostActiveAssetId,
      assetIds,
      activeAssetId,
    } = this.props;
    this.handleIntersectionChange({ isIntersecting });
    window.addEventListener('resize', this.handleWindowResize);
    if (assetIds.length && !activeAssetId) {
      const { activeIndex } = this.state;
      const activeAssetId = assetIds[activeIndex];
      if (activeAssetId) setPostActiveAssetId({ postId, activeAssetId });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isPlayerSeeking, isIntersecting, assetIds } = this.props;
    const { activeIndex } = this.state;
    if (this.swiper && isPlayerSeeking !== prevProps.isPlayerSeeking) {
      this.swiper.allowTouchMove = !isPlayerSeeking;
    }

    if (isIntersecting !== prevProps.isIntersecting) {
      this.handleIntersectionChange({ isIntersecting });
    }

    if (
      activeIndex !== prevState.activeIndex ||
      assetIds !== prevProps.assetIds
    ) {
      const { postId, assetIds, setPostActiveAssetId } = this.props;
      const activeAssetId = assetIds?.[activeIndex];
      if (activeAssetId) setPostActiveAssetId({ postId, activeAssetId });
    }
  }

  componentWillUnmount() {
    this.removeIntersectingData();
    this.handleOnMouseMove.cancel();
    this.handleWindowResize.cancel();
    clearTimeout(this.navigatorDisplayTimeout);
    window.removeEventListener('scroll', this.updateIntersectingData);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.swiper) this.swiper?.destroy();
  }

  handleOnClick = e => {
    e.stopPropagation();
    const { isOnPostDetail } = this.props;
    if (!isOnPostDetail) {
      const { postId, push } = this.props;
      return push({
        pathname: `/post/${postId}`,
        search: window.location.search,
      });
    }
  };

  resizePostMediaHeight = ({ target }) => {
    if (!target) return;

    const { postId, setMediaHeightInPostFeed } = this.props;
    const width = target.naturalWidth;
    const height = target.naturalHeight;
    const aspectRatio = Math.min(Math.max(width / height, 9 / 16), 16 / 9);
    let mediaHeight = this.selfRef.current.clientWidth / aspectRatio;

    if (isNaN(mediaHeight)) mediaHeight = this.selfRef.current.clientHeight;

    setMediaHeightInPostFeed({ postId, mediaHeight: `${mediaHeight}px` });
  };

  getPercentageInViewport = () => {
    if (!this.selfRef?.current) return;

    const { top, bottom, left, right, height, width } =
      this.selfRef.current.getBoundingClientRect();
    const inViewBottom =
      bottom > window.innerHeight ? window.innerHeight : bottom;
    const inViewTop = top < 0 ? 0 : top;
    const inViewHeight = inViewBottom - inViewTop;

    const inViewRight = right > window.innerWidth ? window.innerWidth : right;
    const inViewLeft = left < 0 ? 0 : left;
    const inViewWidth = inViewRight - inViewLeft;
    return (inViewWidth * inViewHeight) / (width * height);
  };

  updateIntersectingData = throttle(() => {
    if (!this.selfRef?.current) return;

    const { postId, setPostMediaIntersectingData, isIntersecting } = this.props;
    const { top, bottom } = this.selfRef.current.getBoundingClientRect();

    const inViewportPercentage = this.getPercentageInViewport({
      ref: this.selfRef,
    });
    return setPostMediaIntersectingData({
      postId,
      isIntersecting,
      top,
      bottom,
      inViewportPercentage,
    });
  }, 200); // TODO: remote config

  removeIntersectingData = () => {
    const { postId, setPostMediaIntersectingData } = this.props;
    return setPostMediaIntersectingData({
      postId,
      isIntersecting: false,
    });
  };

  handleIntersectionChange = ({ isIntersecting }) => {
    const { isOnPostDetail } = this.props;
    this.updateIntersectingData();
    if (!isOnPostDetail) {
      if (isIntersecting) {
        window.addEventListener('scroll', this.updateIntersectingData);
      } else {
        window.removeEventListener('scroll', this.updateIntersectingData);
      }
    }
  };

  handleOnMouseMove = throttle(() => {
    this.setState({
      isMouseMoving: true,
    });
    clearTimeout(this.navigatorDisplayTimeout);
    this.navigatorDisplayTimeout = setTimeout(() => {
      this.setState({
        isMouseMoving: false,
      });
    }, 2000); // TODO: remote config
  }, 1000); // TODO: remote config

  handleWindowResize = throttle(() => {
    if (!this.imageElementRef || !this.selfRef.current) return;
    const { postId, setMediaHeightInPostFeed } = this.props;
    setMediaHeightInPostFeed({
      messageId: postId,
      imageElement: this.imageElementRef,
      mediaWrapperElement: this.selfRef.current,
    });
  }, 500);

  slideNext = () => {
    this.swiper?.slideNext();
  };

  slidePrev = () => {
    this.swiper?.slidePrev();
  };

  renderMedia({ assetId, index }) {
    const {
      postId,
      isOnPostDetail,
      isOnMobile,
      setMediaHeightInPostFeed,
      isIntersected,
    } = this.props;
    const { activeIndex, playerContext } = this.state;
    const enabledImageFullscreenModal = isOnPostDetail && isOnMobile;
    const shouldDisplayCoverPlayer = isOnMobile || !isOnPostDetail;
    const isActive = index === activeIndex;
    const objectFit = shouldDisplayCoverPlayer ? 'cover' : 'contain';
    return (
      <PlayerContext.Provider value={playerContext}>
        <PlayerWrapper
          data-element_id={!isOnPostDetail && ButtonId.Post.ButtonPostMedia}
          data-tracking_payload={
            !isOnPostDetail && {
              messageId: postId,
            }
          }
          onClick={this.handleOnClick}
        >
          <PostVideoPlayer
            postId={postId}
            assetId={assetId}
            isActive={isActive}
            enabledImageFullscreenModal={enabledImageFullscreenModal}
            resizePostMediaHeight={({ target }) => {
              this.imageElementRef = target;
              setMediaHeightInPostFeed({
                messageId: postId,
                imageElement: target,
                mediaWrapperElement: this.selfRef.current,
              });
            }}
            objectFit={objectFit}
            index={index}
            isIntersected={isIntersected}
          />
        </PlayerWrapper>
      </PlayerContext.Provider>
    );
  }

  renderMultipleMedias() {
    const {
      assetIds,
      isOnPostDetail,
      isOnMobile,
      isActiveAssetVideoType,
      activeAssetId,
    } = this.props;
    const shouldEnableKeyboard =
      isOnPostDetail && !isOnMobile && !isActiveAssetVideoType;

    let initialIndex = 0;
    if (activeAssetId) {
      const index = assetIds.findIndex(assetId => assetId === activeAssetId);
      initialIndex = index > -1 ? index : 0;
    }
    return (
      <StyledSwiper
        modules={['Pagination', 'Virtual', 'Keyboard']}
        onSwiper={swiper => {
          this.swiper = swiper;
          this.setState({
            activeIndex: swiper.activeIndex,
          });
        }}
        initialSlide={initialIndex}
        pagination={{
          type: 'bullets',
          dynamicBullets: true,
          dynamicMainBullets: 3,
          clickable: true,
        }}
        virtual={{
          addSlidesAfter: 1,
          addSlidesBefore: 1,
        }}
        touchStartPreventDefault={false}
        onTransitionEnd={swiper => {
          this.setState({
            activeIndex: swiper.activeIndex,
          });
        }}
        keyboard={{
          enabled: shouldEnableKeyboard,
          onlyInViewport: true,
        }}
        detail={isOnPostDetail ? isOnPostDetail.toString() : undefined}
      >
        {assetIds.map((assetId, index) => {
          return (
            <SwiperSlide key={index} virtualIndex={index}>
              {this.renderMedia({ assetId, index })}
            </SwiperSlide>
          );
        })}
      </StyledSwiper>
    );
  }

  renderSideNavigators = () => {
    const { assetIds, isOnMobile } = this.props;
    const { isMouseMoving, activeIndex } = this.state;

    const shouldShowNavigators =
      assetIds.length > 1 && isMouseMoving && !isOnMobile;
    const shouldDiaplayLeft = activeIndex !== 0;
    const shouldDiaplayRight = activeIndex !== assetIds.length - 1;
    if (shouldShowNavigators) {
      return (
        <>
          {
            <TopFraction>
              {activeIndex + 1}/{assetIds.length}
            </TopFraction>
          }
          {shouldDiaplayLeft ? (
            <SideNavigatorLeft onClick={this.slidePrev} left>
              <ArrowIconButton left>
                <ArrowIcon src={ArrowIconSrc} left />
              </ArrowIconButton>
            </SideNavigatorLeft>
          ) : null}
          {shouldDiaplayRight && (
            <SideNavigatorRight onClick={this.slideNext}>
              <ArrowIconButton>
                <ArrowIcon src={ArrowIconSrc} />
              </ArrowIconButton>
            </SideNavigatorRight>
          )}
        </>
      );
    }
    return null;
  };

  renderPosterPlaceholder = () => {
    const {
      postId,
      thumbnail,
      setMediaHeightInPostFeed,
      thumbnailSize,
      isOnPostDetail,
    } = this.props;
    const { isThumbnailLoaded } = this.state;
    const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
      MediaAssetFormat.AVIF,
      MediaAssetFormat.WEBP, // actually in this case .webp would be smaller
      MediaAssetFormat.JPG,
    ].map(format =>
      getPublicSrcSet({ href: thumbnail, size: thumbnailSize, format })
    );

    const desktopThumbnailSize = thumbnailSize * 2;

    const [avifSrcSetDesktop, webpSrcSetDesktop] = [
      MediaAssetFormat.AVIF,
      MediaAssetFormat.WEBP,
    ].map(format =>
      getPublicSrcSet({ href: thumbnail, size: desktopThumbnailSize, format })
    );
    return (
      <PosterPlaceholder
        isOnPostDetail={isOnPostDetail}
        shouldHidePoster={isThumbnailLoaded}
      >
        <picture>
          <DecryptionWrapper resourceUrl={jpgSrcSet.src}>
            <source
              srcSet={avifSrcSetDesktop.srcSet}
              type="image/avif"
              media={`(min-width: ${breakpoint.mobile}px)`}
            />
            <source
              srcSet={webpSrcSetDesktop.srcSet}
              type="image/webp"
              media={`(min-width: ${breakpoint.mobile}px)`}
            />
            <source type="image/avif" srcSet={avifSrcSet.srcSet} />
            <source type="image/webp" srcSet={webpSrcSet.srcSet} />
            <img
              {...jpgSrcSet}
              aria-hidden
              // eslint-disable-next-line react/no-unknown-property
              fetchpriority="high"
              onLoad={({ target }) => {
                this.imageElementRef = target;
                this.setState({ isThumbnailLoaded: true });
                return setMediaHeightInPostFeed({
                  messageId: postId,
                  imageElement: target,
                  mediaWrapperElement: this.selfRef.current,
                });
              }}
            />
          </DecryptionWrapper>
        </picture>
      </PosterPlaceholder>
    );
  };

  render() {
    const {
      assetIds,
      isOnPostDetail,
      isIntersected,
      isOnMobile,
      mediaHeightInPostFeed,
    } = this.props;
    const { isThumbnailLoaded } = this.state;
    if (!assetIds?.length) {
      return (
        <StyledPostMedia>{this.renderPosterPlaceholder()}</StyledPostMedia>
      );
    }

    const shouldDisplayCoverPlayer = isOnMobile || !isOnPostDetail;
    const shouldDisplaySideNavigator =
      !isOnMobile && isOnPostDetail && assetIds.length > 1;

    const defaultHeight =
      !isServer && window.innerWidth < breakpoint.tablet
        ? `${(Math.min(window.innerWidth, 680) * 9) / 16}px`
        : '100%';

    return (
      <StyledPostMedia
        ref={element => {
          if (element) {
            this.selfRef.current = element;
            this.updateIntersectingData();
          }
        }}
        height={
          isOnPostDetail
            ? shouldDisplayCoverPlayer && mediaHeightInPostFeed
              ? `${mediaHeightInPostFeed}px`
              : defaultHeight
            : '100%'
        }
        objectFit={shouldDisplayCoverPlayer ? 'cover' : 'contain'}
        onMouseMove={this.handleOnMouseMove}
      >
        {(isOnPostDetail ? isIntersected && isThumbnailLoaded : isIntersected)
          ? assetIds.length > 1
            ? this.renderMultipleMedias()
            : this.renderMedia({ assetId: assetIds[0], index: 0 })
          : null}
        {isIntersected &&
          shouldDisplaySideNavigator &&
          this.renderSideNavigators()}
        {/* This element is used to determine the LCP score */}
        {isOnPostDetail && this.renderPosterPlaceholder()}
      </StyledPostMedia>
    );
  }
}

PostMedia.propTypes = {
  postId: PropTypes.string,
  isOnMobile: PropTypes.bool,
  isOnPostDetail: PropTypes.bool,
  isIntersecting: PropTypes.bool,
  isIntersected: PropTypes.bool,
  isPlayerSeeking: PropTypes.bool,
  assetIds: PropTypes.array,
  push: PropTypes.func,
  setPostMediaIntersectingData: PropTypes.func,
  mediaHeightInPostFeed: PropTypes.number,
  setPostActiveAssetId: PropTypes.func,
  setMediaHeightInPostFeed: PropTypes.func,
  activeAssetId: PropTypes.string,
  isActiveAssetVideoType: PropTypes.bool,
  thumbnail: PropTypes.string,
  thumbnailSize: PropTypes.number,
};

PostMedia.defaultProps = {
  postId: null,
  isOnMobile: true,
  isOnPostDetail: false,
  isIntersecting: false,
  isIntersected: false,
  isPlayerSeeking: false,
  assetIds: [],
  push: () => null,
  setPostMediaIntersectingData: () => null,
  mediaHeightInPostFeed: 0,
  setPostActiveAssetId: () => null,
  setMediaHeightInPostFeed: () => null,
  isActiveAssetVideoType: null,
  activeAssetId: null,
  thumbnail: null,
  thumbnailSize: 256,
};

const StyledPostMedia = styled.div.attrs(({ height = '100%' }) => ({
  style: { height },
}))`
  position: relative;
  width: 100%;

  .videojs {
    width: inherit;
    height: inherit;
    outline: 0;
    font-size: 0px;
  }

  video {
    width: inherit;
    height: inherit;
    object-fit: ${({ objectFit }) => objectFit};
    background: ${color.black};
    display: block; // Fix video height is 0px on iOS safari sometimes.
  }
`;

const StyledSwiper = styled(Swiper)`
  ${SwiperPaginationDefaultStyle};

  padding-bottom: 36px;
  height: calc(100% + 36px);

  .swiper-pagination-bullets {
    bottom: 8px;

    .swiper-pagination-bullet {
      margin: 0 2px;
    }
  }

  @media (min-width: ${breakpoint.tablet}px) {
    ${({ detail: isOnPostDetail }) =>
      isOnPostDetail
        ? css`
            height: 100%;
            padding-bottom: 0;
            .swiper-pagination {
              display: none;
            }
          `
        : null}
  }
`;

const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${color.black};
`;

const TopFraction = styled.div`
  position: absolute;
  top: 24px;
  left: 50%;

  transform: translateX(-50%);

  font-size: 16px;
  line-height: 1;
  color: ${color.white};
  z-index: 1;
`;

const SideNavigator = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  position: absolute;
  top: 0;
  bottom: 64px;
  z-index: 1;
  animation: 0.6s forwards ${FadeIn};

  ${media.tablet`
    display: none;
  `}
  @media (hover: hover) {
    &:hover {
      > div {
        > img {
          opacity: 1;
        }
        ${({ left }) =>
          left
            ? css`
                transform: translateX(-5px);
              `
            : css`
                transform: translateX(5px);
              `};
      }
    }
  }
`;

const SideNavigatorLeft = styled(SideNavigator)`
  left: 0;
`;
const SideNavigatorRight = styled(SideNavigator)`
  right: 0;
`;

const ArrowIconButton = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(100, 100, 100, 0.2);
  transition: 0.3s;
  > img {
    opacity: 0.5;
  }
`;

const ArrowIcon = styled.img`
  width: 40px;
  height: 40px;
  ${({ left }) =>
    left
      ? css`
          transform: rotate(180deg);
        `
      : null};
`;

const PosterPlaceholder = styled.div.attrs(({ shouldHidePoster }) => ({
  style: {
    opacity: shouldHidePoster ? 0 : 1,
    zIndex: shouldHidePoster ? -1 : 2,
  },
}))`
  transition: opacity 0.2s;
  pointer-events: none;
  position: absolute;
  inset: 0px;

  picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;

    ${({ isOnPostDetail }) => {
      if (isOnPostDetail) {
        return css`
          ${media.tablet`
            min-height: 350px;
            object-fit: contain;
          `}

          // Ensure the min-height matches the final rendered height for LCP calculation
          ${media.mobile`
            min-height: 480px;
            object-fit: cover;
          `}

          // Ensure responsiveness on extremely small screens
          @media(max-width: 320px) {
            min-height: 200px;
            object-fit: cover;
          }
        `;
      }
    }}
  }
`;

export default PostMedia;
